import { useCallback, useState } from 'react'
import {
  createSunoPrompt,
  getSunoPromptGroups,
  suggestSunoPromptMetadata,
  updateSunoPrompt,
  type SunoPromptGroup,
  type SunoStylePromptSuggestion
} from '../../../api/suno'
import type { PromptEditorState } from '../PromptEditorDialog'

type UseGeneratedPromptSaveOptions = {
  toast: (message: string) => void
}

export const useGeneratedPromptSave = ({ toast }: UseGeneratedPromptSaveOptions) => {
  const [sunoPromptGroups, setSunoPromptGroups] = useState<SunoPromptGroup[]>([])
  const [promptEditor, setPromptEditor] = useState<PromptEditorState | null>(null)
  const [promptSaving, setPromptSaving] = useState(false)

  const loadSunoPromptGroups = useCallback(async (): Promise<void> => {
    try {
      setSunoPromptGroups(await getSunoPromptGroups())
    } catch {
      // Prompt groups are optional metadata for generated prompt saves.
    }
  }, [])

  const openGeneratedPromptSaveDialog = (suggestion: SunoStylePromptSuggestion): void => {
    void loadSunoPromptGroups()
    setPromptEditor({
      id: null,
      title: suggestion.title,
      description: suggestion.explanation,
      groupIds: [],
      stylePrompt: suggestion.style_prompt,
      excludePrompt: suggestion.exclude_prompt ?? ''
    })
  }

  const savePrompt = async (input: PromptEditorState): Promise<void> => {
    const title = input.title.trim()
    const style = input.stylePrompt.trim()

    if (!title || !style) {
      return
    }

    setPromptSaving(true)
    try {
      const payload = {
        title,
        description: input.description.trim() || null,
        sunoGroupIds: input.groupIds,
        style_prompt: style,
        exclude_prompt: input.excludePrompt.trim() || null
      }

      if (input.id) {
        await updateSunoPrompt({
          promptId: input.id,
          ...payload
        })
        toast(`Updated prompt "${title}".`)
      } else {
        await createSunoPrompt(payload)
        toast(`Saved prompt "${title}".`)
      }

      setPromptEditor(null)
      await loadSunoPromptGroups()
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to save prompt.')
    } finally {
      setPromptSaving(false)
    }
  }

  const suggestPromptMetadata = async (input: {
    excludePrompt: string
    stylePrompt: string
  }): Promise<{ title: string; description: string }> => {
    const result = await suggestSunoPromptMetadata({
      style_prompt: input.stylePrompt,
      exclude_prompt: input.excludePrompt || null
    })

    return {
      title: result.title,
      description: result.description
    }
  }

  return {
    sunoPromptGroups,
    promptEditor,
    promptSaving,
    openGeneratedPromptSaveDialog,
    savePrompt,
    setPromptEditor,
    suggestPromptMetadata
  }
}
